import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Inbox, Headphones, ListTree, Sparkles, FileText, Activity,
    Building2, Layers, Shield, Phone, Palette, Users, Gauge, BarChart3,
    Library, Hotel, Utensils, Map, Image as ImageIcon, FileUp,
    MessageCircle, Mic, Brain, Bookmark, BookOpen,
    Smartphone, Mail, Bot, Webhook, FileCheck2,
    LineChart, Compass, Star, ShieldCheck,
    KeyRound, Heart, ArrowUpRight, LogOut,
} from "lucide-react";
import { useConsoleAuth } from "@/lib/console-auth";
import TenantsManager from "@/pages/console/TenantsManager";
import ConsoleInbox from "@/pages/console/ConsoleInbox";
import CsvMigration from "@/pages/console/CsvMigration";
import "@/styles/console.css";

/* ─────────────────────────────────────────────────────────────────────────────
   Concierge® Console — Command Center

   Modelled after the TDC admin pattern: categorised nav grid, dark restricted
   surface, every tile is either ACTIVE (clickable, opens a workspace) or
   COMING SOON (greyed). Phase 1 ships Tenants + Inbox active; everything else
   is scaffolded with proper data-testids ready for Phase 2/3.
   ───────────────────────────────────────────────────────────────────────── */

const CATEGORIES = [
    {
        label: "Command Center",
        color: "#a267f5",
        tiles: [
            { key: "dashboard",       label: "Dashboard",         icon: LayoutDashboard, active: true },
            { key: "inbox",           label: "Unified Inbox",     icon: Inbox,           active: true },
            { key: "service-desk",    label: "Service Desk",      icon: Headphones,      active: false },
            { key: "pillar-queues",   label: "Pillar Queues",     icon: ListTree,        active: false },
            { key: "mira-intel",      label: "Mira Intelligence", icon: Sparkles,        active: false },
            { key: "documents",       label: "Documents",         icon: FileText,        active: false },
            { key: "health",          label: "Health",            icon: Activity,        active: false },
        ],
    },
    {
        label: "Tenants",
        color: "#c9a96a",
        tiles: [
            { key: "tenants",         label: "Tenants",           icon: Building2,       active: true },
            { key: "tiers",           label: "Tiers",             icon: Layers,          active: false, navigate: "tenants" },
            { key: "pillar-rights",   label: "Pillar Rights",     icon: Shield,          active: false, navigate: "tenants" },
            { key: "contact-routing", label: "Contact Routing",   icon: Phone,           active: false, navigate: "tenants" },
            { key: "brand",           label: "Brand",             icon: Palette,         active: false, navigate: "tenants" },
            { key: "members",         label: "Members",           icon: Users,           active: false },
            { key: "quotas",          label: "Quotas",            icon: Gauge,           active: false, navigate: "tenants" },
            { key: "usage",           label: "Usage",             icon: BarChart3,       active: false },
        ],
    },
    {
        label: "Listings & Archive",
        color: "#7dd3fc",
        tiles: [
            { key: "clubs",           label: "Clubs (3,503)",     icon: Library,         active: false },
            { key: "hotels",          label: "Hotels",            icon: Hotel,           active: false },
            { key: "restaurants",     label: "Restaurants",       icon: Utensils,        active: false },
            { key: "experiences",     label: "Experiences",       icon: Map,             active: false },
            { key: "places-cache",    label: "Places Cache",      icon: ImageIcon,       active: false },
            { key: "csv-migration",   label: "CSV Migration",     icon: FileUp,          active: true },
        ],
    },
    {
        label: "Mira & AI",
        color: "#f472b6",
        tiles: [
            { key: "mira-chats",      label: "Mira Chats",        icon: MessageCircle,   active: false },
            { key: "live-threads",    label: "Live Threads",      icon: Mic,             active: false },
            { key: "voice-config",    label: "Voice Config",      icon: Brain,           active: false },
            { key: "banned-phrases",  label: "Banned Phrases",    icon: ShieldCheck,     active: false },
            { key: "pillar-mapping",  label: "Pillar Mapping",    icon: Bookmark,        active: false },
            { key: "memory",          label: "Memory",            icon: Brain,           active: false },
            { key: "doctrine",        label: "Doctrine Editor",   icon: BookOpen,        active: false },
        ],
    },
    {
        label: "Channels",
        color: "#34d399",
        tiles: [
            { key: "whatsapp",        label: "WhatsApp",          icon: Smartphone,      active: false },
            { key: "email",           label: "Email",             icon: Mail,            active: false },
            { key: "chatbase",        label: "Chatbase",          icon: Bot,             active: false },
            { key: "webhooks",        label: "Webhooks",          icon: Webhook,         active: false },
            { key: "templates",       label: "Templates",         icon: FileCheck2,      active: false },
        ],
    },
    {
        label: "Analytics",
        color: "#fbbf24",
        tiles: [
            { key: "live-mis",        label: "Live MIS",          icon: LineChart,       active: false },
            { key: "reports",         label: "Reports",           icon: FileText,        active: false },
            { key: "trajectories",    label: "Member Trajectories", icon: Compass,       active: false },
            { key: "top-patterns",    label: "Top Patterns",      icon: BarChart3,       active: false },
            { key: "conversion",      label: "Conversion Funnel", icon: LineChart,       active: false },
            { key: "reviews",         label: "Reviews",           icon: Star,            active: false },
        ],
    },
    {
        label: "Config",
        color: "#f87171",
        tiles: [
            { key: "api-keys",        label: "API Keys",          icon: KeyRound,        active: false },
            { key: "system-health",   label: "System Health",     icon: Activity,        active: false },
            { key: "backup",          label: "Backup",            icon: FileText,        active: false },
            { key: "agents",          label: "Agents",            icon: Users,           active: false },
            { key: "concierge-xp",    label: "Concierge® XP",     icon: Heart,           active: false },
        ],
    },
];

export default function ConsoleDashboard() {
    const navigate = useNavigate();
    const { user, loading, logout } = useConsoleAuth();
    const [view, setView] = useState("dashboard");
    const [stats, setStats] = useState({ tenants: 0, members: 0, tickets: 0 });

    useEffect(() => {
        if (!loading && !user) {
            navigate("/mira/console/login", { replace: true });
        }
    }, [loading, user, navigate]);

    // Quick stats — tenants count from /api/console/tenants
    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const { consoleApi } = await import("@/lib/console-auth");
                const { data } = await consoleApi.get("/console/tenants");
                setStats((s) => ({ ...s, tenants: data.count || 0 }));
            } catch {
                /* non-fatal */
            }
        })();
    }, [user]);

    if (loading || !user) {
        return (
            <div className="console-root flex items-center justify-center min-h-[60svh]">
                <p className="console-eyebrow" data-testid="console-loading">Loading…</p>
            </div>
        );
    }

    const onTileClick = (tile) => {
        if (tile.navigate) {
            setView(tile.navigate);
            return;
        }
        if (!tile.active) return;
        setView(tile.key);
    };

    return (
        <div className="console-root" data-testid="console-dashboard-page">
            {/* Top bar */}
            <header className="border-b" style={{ borderColor: "var(--console-border)" }}>
                <div className="max-w-[1400px] mx-auto px-8 py-5 flex items-center justify-between">
                    <button
                        onClick={() => setView("dashboard")}
                        className="flex items-center gap-3 text-left"
                        data-testid="console-home-button"
                    >
                        <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ background: "linear-gradient(135deg, #c9a96a 0%, #e0c082 100%)" }}
                        >
                            <span style={{ color: "#0b1020", fontWeight: 700, fontSize: 14 }}>LC</span>
                        </div>
                        <div>
                            <p style={{ fontFamily: "Georgia, serif", fontSize: 18, fontWeight: 600 }}>
                                Concierge<sup style={{ fontSize: "0.5em" }}>®</sup> Console
                            </p>
                            <p className="console-eyebrow mt-[2px]">Command Center</p>
                        </div>
                    </button>

                    <div className="flex items-center gap-4">
                        <span className="console-pill console-pill-muted" data-testid="console-user-pill">
                            {user.email}
                        </span>
                        <button
                            onClick={() => navigate("/")}
                            className="console-btn-ghost"
                            data-testid="console-view-site"
                        >
                            <ArrowUpRight size={14} className="inline mr-2" /> Main site
                        </button>
                        <button
                            onClick={() => { logout(); navigate("/mira/console/login"); }}
                            className="console-btn-ghost"
                            data-testid="console-logout"
                        >
                            <LogOut size={14} className="inline mr-2" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            {/* Body */}
            <div className="max-w-[1400px] mx-auto px-8 py-10">
                {view === "dashboard" && (
                    <DashboardGrid stats={stats} onTileClick={onTileClick} />
                )}
                {view === "tenants" && (
                    <Workspace
                        title="Tenants"
                        eyebrow="Multi-tenant configuration · pillar rights · tiers · contact routing"
                        onBack={() => setView("dashboard")}
                    >
                        <TenantsManager />
                    </Workspace>
                )}
                {view === "inbox" && (
                    <Workspace
                        title="Unified Inbox"
                        eyebrow="Every Mira request · Chatbase · WhatsApp · web basket · email"
                        onBack={() => setView("dashboard")}
                    >
                        <ConsoleInbox />
                    </Workspace>
                )}
                {view === "csv-migration" && (
                    <Workspace
                        title="CSV Migration"
                        eyebrow="Export · import · production-safe data portability"
                        onBack={() => setView("dashboard")}
                    >
                        <CsvMigration />
                    </Workspace>
                )}
            </div>
        </div>
    );
}

function DashboardGrid({ stats, onTileClick }) {
    const totalTiles = useMemo(
        () => CATEGORIES.reduce((n, c) => n + c.tiles.length, 0),
        []
    );
    const activeTiles = useMemo(
        () => CATEGORIES.reduce((n, c) => n + c.tiles.filter((t) => t.active || t.navigate).length, 0),
        []
    );

    return (
        <div data-testid="console-grid">
            {/* Quick stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12" data-testid="console-stats">
                <StatCard label="Active Tenants"  value={stats.tenants} testId="stat-tenants" />
                <StatCard label="Total Tiles"     value={totalTiles}    testId="stat-tiles-total" />
                <StatCard label="Active Workspaces" value={activeTiles} testId="stat-tiles-active" />
                <StatCard label="Brain"           value="GPT-5.1"       testId="stat-brain" />
            </div>

            {/* Category grid */}
            <div className="space-y-6">
                {CATEGORIES.map((cat) => (
                    <div
                        key={cat.label}
                        className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 items-start"
                        data-testid={`console-category-${cat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                        <div
                            className="console-category-label"
                            style={{ color: cat.color, borderColor: `${cat.color}55`, background: `${cat.color}1a` }}
                        >
                            {cat.label}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {cat.tiles.map((tile) => {
                                const Icon = tile.icon;
                                const clickable = tile.active || tile.navigate;
                                return (
                                    <button
                                        key={tile.key}
                                        onClick={() => onTileClick(tile)}
                                        disabled={!clickable}
                                        className={`console-tile ${clickable ? "" : "console-tile-coming"}`}
                                        data-testid={`console-tile-${tile.key}`}
                                        data-active={clickable ? "true" : "false"}
                                    >
                                        <Icon size={16} strokeWidth={1.7} style={{ color: clickable ? "var(--console-brass)" : "var(--console-text-muted)" }} />
                                        <span>{tile.label}</span>
                                        {tile.active && (
                                            <span className="ml-auto" style={{ width: 6, height: 6, borderRadius: 6, background: "var(--console-success)" }} />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatCard({ label, value, testId }) {
    return (
        <div className="console-card p-5" data-testid={testId}>
            <p className="console-eyebrow">{label}</p>
            <p className="mt-2 text-3xl" style={{ fontFamily: "Georgia, serif", color: "var(--console-brass-bright)" }}>
                {value}
            </p>
        </div>
    );
}

function Workspace({ title, eyebrow, onBack, children }) {
    return (
        <div data-testid={`console-workspace-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <button
                        onClick={onBack}
                        className="console-eyebrow flex items-center gap-2 mb-2"
                        data-testid="console-workspace-back"
                        style={{ background: "transparent", border: "none", color: "var(--console-text-muted)" }}
                    >
                        ← Back to dashboard
                    </button>
                    <h2 style={{ fontFamily: "Georgia, serif", fontSize: 32 }}>{title}</h2>
                    <p className="mt-1" style={{ color: "var(--console-text-muted)", fontSize: 13 }}>{eyebrow}</p>
                </div>
            </div>
            {children}
        </div>
    );
}
